/**
 * Läuft die Seite in unserer App? (Sitzung A1, Konzept `docs/strategie/app-konzept.md` §4.1)
 *
 * Die App ist eine Hülle von Web to Native um die mobile Webansicht und im App Store eine Reader-App: In ihr wird nichts
 * verkauft (keine Preise, keine Kaufknöpfe, kein Hinweis auf den Kauf woanders, Frage 395). Erkannt wird sie an der
 * Schnittstelle, die nur Web to Native einbaut (`window.WebToNativeInterface` unter Android,
 * `window.webkit.messageHandlers.webToNativeInterface` unter iOS; so erkennt sie auch das Paket `webtonative`).
 *
 * Früher entschied allein der User-Agent (`isWebView()` in `src/lib/externalBrowser.ts`). Der hielt auch die Browser in
 * Instagram, Facebook und anderen Apps für unsere App, und wer über eine Anzeige kam, sah keine Kaufknöpfe. Der
 * User-Agent zählt deshalb nur noch als Rückfall, falls die Schnittstelle einmal fehlt, und nie, wenn er einen dieser
 * Browser nennt.
 *
 * Diese Datei importiert nichts (sie läuft vor allem anderen und in Tests ohne React).
 */

/** Zum Ausprobieren im Browser: `?app=probe` schaltet für diesen Tab die App-Fassung an, `?app=aus` wieder ab. */
export const APP_PROBE_SCHLUESSEL = "mk-app-probe";

/**
 * Browser in anderen Apps, die sich im User-Agent zu erkennen geben (nie unsere App). `LovableApp` ist die Vorschau in
 * der App von Lovable (18.10.2026 in den Protokollen gesehen): Ohne Eintrag zeigte sie die App-Fassung.
 */
const FREMDE_APP_BROWSER = /Instagram|FBAN|FBAV|FB_IAB|FBIOS|MessengerForiOS|LinkedInApp|Pinterest|Snapchat|musical_ly|BytedanceWebview|TikTok|Twitter|Line\/|LovableApp/i;

/** Eigene Kennung im User-Agent (falls in Web to Native eingetragen). */
const EIGENE_KENNUNG = /Web2Native|WebToNative|MusiKonzept/i;

interface AppFenster {
  WebToNativeInterface?: unknown;
  webkit?: { messageHandlers?: { webToNativeInterface?: unknown } };
}

/** Steckt eine eingebettete Ansicht dahinter (nur nach dem User-Agent)? Gleiche Regel wie `isWebView()`. */
export function siehtAusWieWebView(ua: string): boolean {
  const ios = /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(ua);
  const android = /wv|WebView/i.test(ua) && /Android/i.test(ua);
  return ios || android;
}

/** Entscheidung aus Schnittstelle, User-Agent und Probe-Schalter (ohne `window`, für Tests). */
export function appErkennen(eingabe: { schnittstelle: boolean; ua: string; probe: boolean }): boolean {
  if (eingabe.probe) return true;
  if (eingabe.schnittstelle) return true;
  const ua = eingabe.ua || "";
  if (FREMDE_APP_BROWSER.test(ua)) return false;
  if (EIGENE_KENNUNG.test(ua)) return true;
  return siehtAusWieWebView(ua);
}

function probeLesen(): boolean {
  try {
    const wert = new URLSearchParams(window.location.search).get("app");
    if (wert === "probe") window.sessionStorage.setItem(APP_PROBE_SCHLUESSEL, "1");
    if (wert === "aus") window.sessionStorage.removeItem(APP_PROBE_SCHLUESSEL);
    return window.sessionStorage.getItem(APP_PROBE_SCHLUESSEL) === "1";
  } catch {
    return false;
  }
}

/** Läuft die Seite in unserer App (Web to Native)? */
export function inUnsererApp(): boolean {
  if (typeof window === "undefined") return false;
  const w = window as unknown as AppFenster;
  const schnittstelle = Boolean(w.WebToNativeInterface) || Boolean(w.webkit?.messageHandlers?.webToNativeInterface);
  const ua = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
  return appErkennen({ schnittstelle, ua, probe: probeLesen() });
}

/** Darf hier verkauft werden (Preise, Kaufknöpfe, Kasse)? In der App nie. */
export function verkaufErlaubt(): boolean {
  return !inUnsererApp();
}

/**
 * Die App kennt nur den Mitgliederbereich (Frage 398). Öffentliche Seiten leiten in der App um: angemeldet auf
 * `/mein-bereich`, sonst auf `/app`. Erlaubt bleiben Anmelden, kostenloses Konto, Passwort, Rechtstexte, Konto löschen
 * und was zum Mitgliederbereich gehört.
 */
const APP_ERLAUBT: readonly string[] = [
  "/app",
  "/login-app",
  "/login",
  "/sign_in",
  "/users/sign_in",
  "/registrieren",
  "/sign_up",
  "/users/sign_up",
  "/auth",
  "/mein-bereich",
  "/admin",
  "/partner",
  "/impressum",
  "/datenschutz",
  "/agb",
  "/widerruf",
  "/vertrag-widerrufen",
  "/vertrag-kuendigen",
  "/konto-loeschen",
  "/daten-loeschen",
  "/kontakt",
  "/kita-post-abbestellen",
  "/zertifikat",
  "/qr",
  "/kurs-vorschau",
];

/** Gehört der Pfad zu dem, was die App zeigt? `/partner` heißt `/partner` und `/partner/…`, nicht `/partner-werden`. */
export function inDerAppErlaubt(pfad: string): boolean {
  return APP_ERLAUBT.some((p) => pfad === p || pfad.startsWith(`${p}/`));
}

/** Wohin es nach dem Abmelden geht: in der App auf den Start der App, im Browser auf die Anmeldung. */
export function abmeldenZiel(): string {
  return inUnsererApp() ? "/app" : "/login";
}

/** Wohin eine Seite in der App umleitet, oder `null`, wenn sie bleiben darf. Die Hilfe der Webseite wird die Hilfe im Mitgliederbereich. */
export function appUmleitung(pfad: string, angemeldet: boolean): string | null {
  if (inDerAppErlaubt(pfad)) return null;
  if (pfad === "/hilfe" || pfad.startsWith("/hilfe/")) return angemeldet ? "/mein-bereich/hilfe" : "/app";
  return angemeldet ? "/mein-bereich" : "/app";
}
